<template>
  <v-chart ref="chart" :options="options" :init="chartInit" :echart.sync="echart" :ready="chartReady" @contextmenu="contextmenu" />
</template>

<script>
import { format, parse, addDays } from 'date-fns';
import chartable from '../../../../mixins/chartable';
import motletable from '../../../../mixins/motletable';
import deepFreezable from '@/mixins/deepFreezable';

export default {
  mixins: [chartable, motletable, deepFreezable],
  props: {
    props: Object,
    system: {
      type: Object,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    values: {
      type: Array,
      required: true,
    },
    xAxis: {
      type: Array,
      required: true,
    },
    grid: {
      type: Object,
      default() {
        return {
          top: '20%',
          bottom: '20px',
          left: '30px',
          right: '5px',
        };
      },
    },
    tooltipContents: {
      type: Array,
      default() {
        return [];
      },
    },
    split: {
      type: Object,
      default() {
        return {};
      },
    },
    motlet: {
      type: Object,
      required: true,
    },
    dateFormat: {
      type: String,
      default: 'yyyy-MM-dd HH:mm:ss',
    },
    areaStyle: {
      type: String,
      default: 'normal',
    },
    contextmenu: {
      type: Function,
      default: e => e,
    },
  },
  data() {
    return {
      options: {},
      chart: null,
      echart: null,
    };
  },
  computed: {
    chartInit() {
      const xAxisData = Array(24)
        .fill('')
        .map((item, index) => {
          format(new Date(1900, 0, 1, index, index == 0 ? 1 : 59), 'yyyy-MM-dd HH:mm:ss');
        });
      return {
        grid: this.grid,
        xAxis: {
          type: 'category',
          axisLabel: {
            interval: (index, value) => index > 0 && value.lastIndexOf('00:00') == 14,
            formatter: value => format(parse(value, this.dateFormat, new Date()), 'HH'),
            fontWeight: 'normal',
            fontSize: 9,
            color: '#bbbbaa',
          },
          data: xAxisData,
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: value => value,
            fontWeight: 'normal',
            fontSize: 9,
            color: '#bbbbaa',
          },
          max: item => {
            let max = Math.floor(item.max + Math.ceil(item.max / 5));
            const size = String(max).length;
            if (size > 2) {
              max = parseInt(
                String(max).substring(0, 2) +
                  Array(size - 2)
                    .fill('0')
                    .join(''),
              );
            }
            return max;
          },
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
            label: { formatter: param => this.axisPointerLabelFormatter(param) },
          },
          formatter: params => this.tooltipFormatter(params),
          position: (pos, params, el, elRect, size) => {
            var obj = { top: 10 };
            obj[['left', 'right'][+(pos[0] < size.viewSize[0] / 2)]] = 30;
            return obj;
          },
        },
        toolbox: {
          itemSize: 0,
          feature: {
            dataZoom: { yAxisIndex: 'none' },
          },
        },
      };
    },
    isMerged() {
      return this.items.every(item => item.active);
    },
    zooming() {
      const xAxis = this.echart?.getOption()?.xAxis[0];
      return xAxis?.rangeStart !== null && xAxis?.rangeEnd !== null;
    },
  },
  watch: {
    zooming: {
      handler: 'changeZoomingHandler',
    },
    zoomRestore: {
      handler: 'changeZoomRestoreHandler',
    },
    items: {
      deep: true,
      handler: 'changItemsHandler',
    },
    isMerged: {
      immediate: true,
      handler: 'changeIsMergedHandler',
    },
    isDark: {
      handler: 'changColorHandler',
    },
    split: {
      handler: 'changeSplitHandler',
    },
  },
  methods: {
    changItemsHandler() {
      this.changColorHandler();
    },
    changeValuesHandler() {
      this.options = this.getOptions();
    },
    async emitNewXAxis() {
      // console.log('I am LDSChart. Before emitting....');
      await this.$emit('makeNewXAxis'); // 부모 Vue가 선언해 놓은 이벤트를 emit한다.
      // console.log('I am LDSChart. After emitting....');
    },
    changeSplitHandler() {
      const valueIndex = this.values.findIndex(v => v.id == this.split.id);
      // values 가 지정되기 이전에는 처리하지 않는다.
      if (valueIndex == -1) return;
      const index = this.items.findIndex(item => item.id == this.split.id);
      // split을 처리하는 도중에 instance 정보가 변경될때 해당 처리를 실행하지 않는다.
      if (index > -1) {
        // const series = this.echart.getOption().series;
        const series = this.options.series[index];
        const value = this.values[index];
        const visualMap = this.options.visualMap[index];
        // chart series 가 지정되기 이전에는 처리하지 않는다.
        if (!series.data) return;
        const ctime = parse(this.xAxis[value.mlp - 1], this.dateFormat, new Date()).getTime();
        // value.mlp가 series.data의 길이와 동일하면 X축의 날짜를 바꾼다.
        if (value.mlp == series.data.length) {
          // console.log('series.data.length == value.mlp 래...');
          this.emitNewXAxis();
          value.mlp = 0; // MarkLine의 포인트는 이제 0번 데이터이다.
        }
        // split time 이 과거 시점일 경우 값만 변경한다.
        if (this.split.ctime <= ctime) {
          // console.log('split.ctime <= ctime 래....');
          const dataIndex = this.xAxis.findIndex(x => parse(x, this.dateFormat, new Date()).getTime() == this.split.ctime);
          series.data[dataIndex][0] = dataIndex;
          series.data[dataIndex][1] = this.split.data[0];
          series.data[dataIndex][2] = this.split.data[1];
          return; // 하고 종료.
        }
        series.data[value.mlp][0] = value.mlp;
        series.data[value.mlp][1] = this.split.data[0];
        series.data[value.mlp][2] = this.split.data[1];
        // 표시 라인 지정
        if (index == 0) {
          series.markLine = { data: [this.getMarkLine(this.xAxis[value.mlp])] };
        }
        value.mlp++;
        visualMap.pieces[0].lte = value.mlp - 1;
        visualMap.pieces[1].gt = value.mlp - 1;
      }
    },
    changeIsMergedHandler() {
      if (!this.options.series) return;
      this.options.series.forEach((series, seriesIndex) => this.changeIsMergedItemHandler(series, this.options.visualMap[seriesIndex], seriesIndex));
    },
    changColorHandler() {
      if (!this.options.series) return;
      this.options.series.forEach((series, seriesIndex) => {
        if (!seriesIndex) {
          series.markLine.data[0].lineStyle.color = this.getReverseThemeColor(this.chartColor.splitArea, 10, 30);
        }
        let vm = this.options.visualMap[seriesIndex];
        if (this.isMerged) this.changeIsMergedItemHandler(series, vm, seriesIndex);
        else this.changColorItemHandler(series, vm, seriesIndex);
      });
    },
    changeIsMergedItemHandler(series, visualMap, seriesIndex) {
      series.stack = this.getStack(seriesIndex);
      const item = this.items[seriesIndex];
      if (seriesIndex == this.items.length - 1) {
        // MarkPoint의 Label의 formatter를 정의한다.
        const formatter = param => {
          let sum = this.values.reduce((acc, v) => {
            acc += (v.data[param.data.coord[0]] || {})[1] || 0;
            return acc;
          }, 0);
          if (this.split.mergeOption == 'avg') return Math.ceil(sum / this.values.length);
          else return sum;
        };
        const color = this.isMerged ? this.chartColor.merge : item.color;
        const opacity = this.isMerged ? 0.8 : item.active ? 0.8 : 0.1;
        series.lineStyle.opacity = opacity;
        visualMap.pieces[0].color = color;
        visualMap.pieces[1].color = this.getGrayColor(this.isMerged ? this.chartColor.merge : item.color);
        series.itemStyle.color = color;
        series.itemStyle.opacity = opacity;
        series.symbolSize = 10;
        series.markPoint.data[0].label.formatter = this.isMerged ? formatter : {};
        series.markPoint.data[0].label.color = this.getReverseThemeColor(this.isMerged ? this.chartColor.splitArea : this.chartColor.splitArea, 10, 60);
        series.markPoint.data[0].itemStyle.color = color;
        series.markPoint.data[0].itemStyle.opacity = this.isMerged ? 0.5 : item.active ? 0.5 : 0.1;
      } else {
        series.markPoint.data[0].label.show = this.isMerged ? false : true;
        series.symbolSize = this.isMerged ? 0 : 10;
        if (this.isMerged) {
          series.lineStyle.opacity = 0;
          series.itemStyle.opacity = 0;
          series.markPoint.data[0].itemStyle.opacity = 0;
        } else {
          this.changColorItemHandler(series, visualMap, seriesIndex);
        }
      }
    },
    changColorItemHandler(series, visualMap, itemIndex) {
      const item = this.items[itemIndex];
      series.lineStyle.opacity = item.active ? 0.8 : 0.1;
      const opacity = item.active ? 0.8 : 0.1;
      visualMap.pieces[0].opacity = opacity;
      visualMap.pieces[1].opacity = opacity;
      series.itemStyle.opacity = opacity;
      series.markPoint.data[0].label.color = this.getReverseThemeColor(this.chartColor.splitArea, 10, 60);
      series.markPoint.data[0].itemStyle.opacity = item.active ? 0.5 : 0.1;
    },
    chartReady(echart) {
      echart.dispatchAction({
        type: 'takeGlobalCursor',
        key: 'dataZoomSelect',
        dataZoomSelectActive: true,
      });
    },
    changeZoomingHandler(active) {
      this.$store.dispatch('common/motlet/setZoomActive', { id: this.motletId, active });
    },
    changeZoomRestoreHandler() {
      if (this.zoomRestore) {
        this.echart.dispatchAction({ type: 'restore' });
        this.$store.dispatch('common/motlet/setZoomRestore', { id: this.motletId, restore: false });
      }
    },
    getOptions() {
      let seriesIndex = 0;
      let series = {};
      let visualMap = {};
      const seriesArray = [];
      const visualMapArray = [];
      this.items.forEach((item, itemIndex) => {
        const value = this.values.find(value => item.id == value.id); // instance id 에 해당하는 데이터
        seriesIndex = itemIndex;
        series = {
          name: `${item.id}`,
          type: 'line',
          stack: this.getStack(seriesIndex),
          // visualMap으로 Line의 색상을 표현하기 위해 lineStyle은 설정하지 않는다.
          // lineStyle: { color: item.color, opacity: 0.8 },
          // 그런데 visualMap.pieces에서 opacity가 먹지 않는다.
          lineStyle: { opacity: 0.8 },
          itemStyle: { color: item.color, opacity: 0.8 },
          markPoint: this.getMarkPoint(item.color),
          markLine: {
            symbol: 'triangle',
            symbolRotate: 180,
            z: 20,
            data: seriesIndex == 0 ? [this.getMarkLine(this.xAxis[value.mlp - 1])] : [], // MarkLine은 처음 한번만 긋는다.
          },
          data: value.data,
        };
        visualMap = {
          show: false,
          dimension: 0, // Y축 value의 각 요소들의 첫번째 값과 연동한다
          seriesIndex, // 각 series 데이터와 연동한다.
          pieces: [
            { lte: value.mlp - 1, color: item.color },
            { gt: value.mlp - 1, lte: value.data.length - 1, color: this.getGrayColor(item.color) },
          ],
        };
        seriesArray.push(series);
        visualMapArray.push(visualMap);
        this.changeIsMergedItemHandler(series, visualMap, itemIndex, seriesIndex);
      });
      return {
        xAxis: {
          data: this.xAxis,
        },
        visualMap: visualMapArray,
        series: seriesArray,
      };
    },
    getStack(seriesIndex) {
      // 전체 인스턴스의 합을 보는 상황에서는 Line 차트를 stack으로 보기 위해 stack 이름을 동일하게 해주고
      // 개별 인스턴스를 보는 상황에서는 합이 보여지면 안되므로 stack 이름을 전부 틀리게 한다.
      return this.isMerged ? 'data' : `${seriesIndex}`;
    },
    getMarkPoint(color) {
      return {
        data: [
          {
            type: 'max',
            label: {
              show: true,
              color: this.getReverseThemeColor(this.chartColor.splitArea, 10, 60),
              fontWeight: 'normal',
              fontSize: 11,
              formatter: {},
            },
            itemStyle: { color, opacity: 0.5 },
          },
        ],
      };
    },
    getMarkLine(xAxisTime) {
      return {
        symbol: 'none',
        lineStyle: {
          color: this.getReverseThemeColor(this.chartColor.splitArea),
          width: 2,
          type: 'solid',
        },
        xAxis: format(new Date(xAxisTime), this.dateFormat),
        label: {
          distance: [0, 10],
          formatter: () => format(new Date(xAxisTime), 'HH:mm'),
        },
      };
    },
    tooltipFormatter(params) {
      let list = [];
      let label = params[0].axisValue; // axisValue -> series[0]의 x축 값.
      const date = parse(label, this.dateFormat, new Date());
      if (this.split.ctime < date.getTime()) label = format(addDays(date, -1), this.dateFormat);

      const contentsList = params =>
        params.map(param => {
          const seriesName = param.seriesName;
          const item = this.items.find(item => item.id == seriesName);
          if (!item) return '';
          let contents = [this.circle(item.color), `<span style="color:${item.color};font-weight:900;">`, item.shortAlias, '</span>'];
          this.tooltipContents.forEach((ttcElm, index) => {
            if (index) contents.push(' , ');
            else contents.push(' ');
            if (ttcElm.name) contents = contents.concat([ttcElm.name]);
            // tooltip에 formatter가 설정되어 있으면 formatter 적용.
            contents = contents.concat([' : ', ttcElm.formatter ? ttcElm.formatter(param.data[index + 1]) : param.data[index + 1]]);
            if (ttcElm.unit) contents = contents.concat([' ', ttcElm.unit]);
          });
          if (!this.tooltipContents.length) contents = contents.concat([': ', param.data[1]]);
          return contents.join('');
        });

      if (this.isMerged) {
        const sum = idx => params.reduce((acc, param) => (acc += param.data[idx + 1]), 0);
        const avg = idx => {
          let sum = params.reduce((acc, param) => (acc += param.data[idx + 1]), 0);
          return Math.ceil(sum / params.length);
        };
        let contents = [this.circle(this.chartColor.merge), `<span style="color:${this.chartColor.merge};font-weight:900;">`, this.system.id, '</span>'];
        this.tooltipContents.forEach((ttcElm, index) => {
          if (index) contents.push(' , ');
          else contents.push(' ');
          if (ttcElm.name) contents = contents.concat([ttcElm.name]);
          contents = contents.concat([' : ', ttcElm.mergeOption == 'avg' ? avg(index) : sum(index)]);
          if (ttcElm.unit) contents = contents.concat([' ', ttcElm.unit]);
        });
        if (!this.tooltipContents.length) contents = contents.concat([': ', params.reduce((acc, param) => (acc += param.value), 0)]);
        list.push(contents.join(''));
        list = list.concat(contentsList(params));
      } else {
        // 활성화 된 item 만 표시
        list = contentsList(
          params.filter(param => {
            const seriesName = param.seriesName;
            return this.items.find(item => item.id == seriesName).active;
          }),
        );
      }
      list.unshift(label);
      return list.join('<br/>');
    },
    axisPointerLabelFormatter(param) {
      let label = '';
      if (param.axisDimension == 'y') label = param.value.toFixed(0);
      else {
        const date = parse(param.value, this.dateFormat, new Date());
        if (this.split.ctime < date.getTime()) label = format(addDays(date, -1), this.dateFormat);
        else label = param.value;
      }
      return label;
    },
    getItemIndex(seriesIndex) {
      return (seriesIndex - (seriesIndex % 2)) / 2;
    },
  },
};
</script>
